import { useEffect, useState, useCallback } from 'react'
import { api } from '../lib/api'
import type { TimeEntry, PaginatedResult } from '@shared/types'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { StatusBadge } from '../components/ui/StatusBadge'
import { GlowCard } from '../components/ui/GlowCard'
import { Badge } from '../components/ui/Badge'
import { useProjectOptions } from '../hooks/useEntityOptions'
import { Select } from '../components/ui/Select'
import { Button } from '../components/ui/Button'

const PlayIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.14v14l11-7-11-7z"/></svg>
const StopIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1"/></svg>

interface TimeEntryWithDetails extends TimeEntry {
  user_name: string
  project_name: string
  project_slug: string
}

export default function TimeTracking() {
  const [entries, setEntries] = useState<TimeEntryWithDetails[]>([])
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null)
  const [loading, setLoading] = useState(true)
  const [clockingIn, setClockingIn] = useState(false)
  const [clockForm, setClockForm] = useState({ project_slug: '', category: 'install', description: '' })
  const [showClockIn, setShowClockIn] = useState(false)
  const { options: projectOptions } = useProjectOptions()

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [entriesRes, activeRes] = await Promise.all([
        api.get<PaginatedResult<TimeEntryWithDetails>>('/time?limit=50'),
        api.get<{ entry: TimeEntry | null }>('/time/active'),
      ])
      setEntries(entriesRes.data)
      setActiveEntry(activeRes.entry)
    } catch { /* handled */ }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  async function handleClockIn(e: React.FormEvent) {
    e.preventDefault()
    setClockingIn(true)
    try {
      await api.post('/time', clockForm)
      setShowClockIn(false)
      setClockForm({ project_slug: '', category: 'install', description: '' })
      loadData()
    } catch { /* error */ }
    finally { setClockingIn(false) }
  }

  async function handleClockOut() {
    if (!activeEntry) return
    try {
      await api.patch(`/time/${activeEntry.slug}`, { clock_out: new Date().toISOString() })
      loadData()
    } catch { /* error */ }
  }

  function formatDuration(minutes: number | null) {
    if (!minutes) return '-'
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return `${h}h ${m}m`
  }

  function formatTime(iso: string) {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' })
  }

  if (loading) return <TableSkeleton cols={7} />

  return (
    <div className="page-enter">
      {!activeEntry && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button onClick={() => setShowClockIn(true)} icon={PlayIcon}>Clock In</Button>
        </div>
      )}

      {activeEntry && (
        <GlowCard style={{ padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--ok)', animation: 'pulse 2s infinite' }} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>Currently clocked in</div>
              <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
                Since {formatTime(activeEntry.clock_in)} &middot; {activeEntry.category}
              </div>
            </div>
          </div>
          <Button variant="danger" onClick={handleClockOut} icon={StopIcon}>Clock Out</Button>
        </GlowCard>
      )}

      {showClockIn && !activeEntry && (
        <GlowCard style={{ padding: 20, marginBottom: 20 }}>
          <form onSubmit={handleClockIn} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Select
                label="Project"
                value={clockForm.project_slug}
                onChange={(e) => setClockForm({ ...clockForm, project_slug: e.target.value })}
                placeholder="Select project..."
                style={{ fontSize: 14 }}
                options={projectOptions}
              />
              <Select
                label="Category"
                value={clockForm.category}
                onChange={(e) => setClockForm({ ...clockForm, category: e.target.value })}
                style={{ fontSize: 14 }}
                options={[
                  { value: 'install', label: 'Install' },
                  { value: 'service', label: 'Service' },
                  { value: 'design', label: 'Design' },
                  { value: 'travel', label: 'Travel' },
                  { value: 'admin', label: 'Admin' },
                  { value: 'other', label: 'Other' },
                ]}
              />
            </div>
            <div>
              <label className="label" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Description</label>
              <input
                value={clockForm.description}
                onChange={(e) => setClockForm({ ...clockForm, description: e.target.value })}
                className="input"
                style={{ fontSize: 14 }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="ghost" type="button" onClick={() => setShowClockIn(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={clockingIn}>
                Start Timer
              </Button>
            </div>
          </form>
        </GlowCard>
      )}

      {entries.length === 0 ? (
        <EmptyState
          title="Track your time"
          description="Clock in when you start a job. Hours are automatically calculated for invoicing."
        />
      ) : (
        <>
          <GlowCard style={{ padding: 0 }} className="hidden sm:block">
            <table className="v3-table">
              <thead>
                <tr>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Date</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Project</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Category</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>In</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Out</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Duration</th>
                  <th style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{formatDate(e.clock_in)}</td>
                    <td style={{ fontSize: 14, color: 'var(--text)' }}>{e.project_name ?? '-'}</td>
                    <td><StatusBadge status={e.category} /></td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatTime(e.clock_in)}</td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{e.clock_out ? formatTime(e.clock_out) : '-'}</td>
                    <td className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatDuration(e.duration_minutes)}</td>
                    <td>
                      {!e.clock_out ? (
                        <Badge v="ok" dot>Active</Badge>
                      ) : e.approved ? (
                        <Badge v="info">Approved</Badge>
                      ) : (
                        <Badge v="warn">Pending</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GlowCard>

          <div className="sm:hidden space-y-3">
            {entries.map((e) => (
              <GlowCard key={e.id} style={{ padding: 16 }}>
                <div className="mobile-card-row">
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatDate(e.clock_in)}</span>
                  {!e.clock_out ? (
                    <span className="badge-success">Active</span>
                  ) : e.approved ? (
                    <span className="badge-info">Approved</span>
                  ) : (
                    <span className="badge-warning">Pending</span>
                  )}
                </div>
                <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>{e.project_name ?? 'No project'}</p>
                <div className="mobile-card-row">
                  <StatusBadge status={e.category} />
                  <span className="mono" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {formatTime(e.clock_in)} - {e.clock_out ? formatTime(e.clock_out) : 'now'}
                  </span>
                </div>
                {e.duration_minutes && (
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatDuration(e.duration_minutes)}</p>
                )}
              </GlowCard>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
